import React from "react"
import {Wrench, Gauge, Shield, CheckCircle} from 'lucide-react'

const BriggsPage = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-700 to-blue-600 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Briggs & Stratton Generator Auto Start Solutions
            </h1>
            <p className="text-xl text-blue-50 leading-relaxed mb-4">
              Remote start and automatic control systems for Briggs & Stratton portable and standby generators.
            </p>
            <p className="text-lg text-blue-100">
              Trusted engine technology with flexible automation options for home backup, RV, and portable power applications.
            </p>
          </div>
        </div>
      </section>

      {/* Compatible Products */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Compatible Briggs & Stratton Generators
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Portable Generators</h3>
              <p className="text-gray-600 mb-3 text-sm">Consumer & Professional Series</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  P Series (2200-4500W)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Q Series Quiet Power
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Elite Series (7000-8500W)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Pro Series (3500-12000W)
                </li>
                <li className="text-xs text-gray-600 mt-2">
                  *Electric start models compatible
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Home Standby</h3>
              <p className="text-gray-600 mb-3 text-sm">Whole House Backup Systems</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Fortress Series (10-20kW)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Symphony II Series
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Built-in auto transfer
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Natural gas or propane
                </li>
                <li className="text-xs text-gray-600 mt-2">
                  *Includes PowerManage technology
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Commercial/Industrial</h3>
              <p className="text-gray-600 mb-3 text-sm">Heavy-Duty Applications</p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Commercial Series (5-20kW)
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Vanguard Engine Powered
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Diesel options available
                </li>
                <li className="flex items-center">
                  <CheckCircle size={18} className="text-blue-600 mr-2" />
                  Remote monitoring capable
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Briggs & Stratton Auto Start Advantages
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
                <Wrench size={32} className="text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Easy Installation</h3>
              <p className="text-gray-600">
                Standardized wiring and control systems across model lines
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
                <Gauge size={32} className="text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Reliable Engines</h3>
              <p className="text-gray-600">
                Industry-leading Vanguard and OHV engine technology
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
                <Shield size={32} className="text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">PowerProtect</h3>
              <p className="text-gray-600">
                Integrated surge protection and voltage regulation
              </p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
                <CheckCircle size={32} className="text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Service Network</h3>
              <p className="text-gray-600">
                Extensive dealer network for parts and support
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Auto Start Options */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Automation Options for Briggs & Stratton Generators
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                PORTABLE MODELS
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Remote Start Kit Installation
              </h3>
              <p className="text-gray-700 mb-4">
                Add wireless remote control to portable generators with electric start systems.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Compatible with all electric start portables</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>300-500ft wireless range</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Simple 2-wire connection to starter solenoid</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Includes weatherproof receiver</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Installation time: 1-2 hours</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow">
              <div className="bg-orange-100 text-orange-600 text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4">
                STANDBY SYSTEMS
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Automatic Transfer Switch
              </h3>
              <p className="text-gray-700 mb-4">
                Factory-integrated auto start with transfer switch for whole-home backup power.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Pre-wired for automatic operation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>10-second power restoration</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>PowerManage load shedding technology</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Weekly self-test exercise mode</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">✓</span>
                  <span>Professional installation required</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Remote Start Installation Guide
          </h2>
          
          <div className="bg-white p-8 rounded-lg shadow-md mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Typical Wiring for Portable Generators
            </h3>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-600 pl-4">
                <h4 className="font-semibold text-gray-800 mb-2">Step 1: Locate Starter Solenoid</h4>
                <p className="text-gray-700 text-sm">
                  Most Briggs & Stratton generators have the starter solenoid mounted near the battery. Look for two large terminals (battery + and starter motor) and two small terminals (control signal).
                </p>
              </div>

              <div className="border-l-4 border-blue-600 pl-4">
                <h4 className="font-semibold text-gray-800 mb-2">Step 2: Connect Remote Start Module</h4>
                <p className="text-gray-700 text-sm">
                  Connect the remote start relay output to the two small solenoid terminals. This parallels the existing start button circuit. Use 14 AWG wire with inline fuse (5A recommended).
                </p>
              </div>

              <div className="border-l-4 border-blue-600 pl-4">
                <h4 className="font-semibold text-gray-800 mb-2">Step 3: Power the Receiver</h4>
                <p className="text-gray-700 text-sm">
                  Connect receiver power to the battery terminals (12V DC). Install an inline fuse (3A) on the positive wire. Mount the receiver in a weatherproof location.
                </p>
              </div>

              <div className="border-l-4 border-blue-600 pl-4">
                <h4 className="font-semibold text-gray-800 mb-2">Step 4: Test Operation</h4>
                <p className="text-gray-700 text-sm">
                  Ensure the ignition switch is in the "Run" position. Test the remote start function. Generator should crank when button is pressed and continue running after release.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
            <p className="text-gray-700">
              <strong>Important:</strong> Briggs & Stratton warranties may be affected by non-professional installation. Consider having a certified dealer perform the installation to maintain coverage.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Automate Your Briggs & Stratton Generator
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Find detailed guides, wiring diagrams, and product recommendations for your specific generator model.
          </p>
        </div>
      </section>
    </div>
  )
}

export default BriggsPage